import React from 'react'
import { Drawer, Descriptions, Divider, Space, Button, Tag, List } from 'antd'
import { CheckCircleOutlined, BankOutlined } from '@ant-design/icons'
import type { Settlement, SettlementOrder } from '../types/settlement.types'
import { formatINR, formatIST } from '../../../shared/utils/formatters'
import { mockOrderDetails } from '../data/mockSettlements'

interface SettlementDetailsDrawerProps {
    open: boolean
    onClose: () => void
    settlement: Settlement | null
    onProcess: (settlement: Settlement) => void
}

export const SettlementDetailsDrawer: React.FC<SettlementDetailsDrawerProps> = ({
    open,
    onClose,
    settlement,
    onProcess
}) => {
    if (!settlement) return null

    return (
        <Drawer
            title="Settlement Details"
            placement="right"
            width={700}
            onClose={onClose}
            open={open}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Descriptions title="Settlement Information" column={1} bordered>
                    <Descriptions.Item label="Settlement ID">
                        <strong>{settlement.id}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Merchant">{settlement.merchant}</Descriptions.Item>
                    <Descriptions.Item label="Number of Orders">
                        {settlement.ordersCount} orders
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Amount">
                        {formatINR(settlement.totalAmount)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Platform Fee (5%)">
                        {formatINR(settlement.platformFee)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Net Amount">
                        <strong style={{ fontSize: 18, color: '#52c41a' }}>
                            {formatINR(settlement.netAmount)}
                        </strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag
                            color={
                                settlement.status === 'completed'
                                    ? 'green'
                                    : settlement.status === 'processing'
                                        ? 'blue'
                                        : settlement.status === 'failed'
                                            ? 'red'
                                            : 'orange'
                            }
                        >
                            {settlement.status.toUpperCase()}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Scheduled Date">
                        {formatIST(settlement.scheduledDate)}
                    </Descriptions.Item>
                    {settlement.completedDate && (
                        <Descriptions.Item label="Completed Date">
                            {formatIST(settlement.completedDate)}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <Descriptions title="Bank Details" column={1} bordered>
                    <Descriptions.Item label="Bank Account">
                        {settlement.bankAccount || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="IFSC Code">
                        {settlement.ifscCode || 'N/A'}
                    </Descriptions.Item>
                    {settlement.utrNumber && (
                        <Descriptions.Item label="UTR Number">
                            {settlement.utrNumber}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                <Divider />

                <div>
                    <h3>Included Orders ({settlement.ordersCount})</h3>
                    <List
                        bordered
                        dataSource={mockOrderDetails}
                        renderItem={(order) => (
                            <List.Item>
                                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                    <div>
                                        <strong>{order.orderId}</strong>
                                        <div style={{ color: '#999', fontSize: 12 }}>{formatIST(order.date)}</div>
                                    </div>
                                    <strong style={{ color: '#1890ff' }}>
                                        {formatINR(order.amount)}
                                    </strong>
                                </Space>
                            </List.Item>
                        )}
                    />
                </div>

                <Divider />

                <Space>
                    {settlement.status === 'pending' && (
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => onProcess(settlement)}
                        >
                            Process Settlement
                        </Button>
                    )}
                    {settlement.status === 'completed' && (
                        <Button icon={<BankOutlined />}>
                            Download Receipt
                        </Button>
                    )}
                </Space>
            </Space>
        </Drawer>
    )
}
